import { Component, ErrorInfo, ReactNode } from "react";

import * as Sentry from "@sentry/react";

import i18n from "@/i18n";

import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";

interface ErrorBoundaryProps {
	children: ReactNode;
	/**
	 * Rendered instead of the default card. Use it where the surrounding chrome
	 * already gives the user somewhere to go. `null` renders nothing, for an
	 * optional surface that must never take the page down with it.
	 */
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	error: Error | null;
}

/**
 * App-wide crash barrier (PREC-3876).
 *
 * The app had no error boundary at all, so a single uncaught render error
 * unmounted the entire React tree and left a blank page — with no report
 * anywhere, because Sentry only sees what the boundary hands it.
 *
 * Mounted twice: around the router (last resort) and around the routed outlet,
 * so a page-level crash keeps the navigation usable and stays recoverable
 * without a reload.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
		console.error(error, errorInfo.componentStack);
	}

	private handleRetry = (): void => {
		this.setState({ error: null });
	};

	private handleReload = (): void => {
		window.location.reload();
	};

	render(): ReactNode {
		if (!this.state.error) {
			return this.props.children;
		}

		if (this.props.fallback !== undefined) {
			return this.props.fallback;
		}

		return (
			<div
				className="flex w-full flex-col items-center justify-center gap-4 p-8"
				data-testid="error-boundary-fallback"
				role="alert"
			>
				<div className="text-center text-md font-bold text-text">
					{i18n.t("errorPages.boundaryTitle")}
				</div>
				<div className="max-w-[480px] text-center text-ssm font-normal text-text-meta">
					{i18n.t("errorPages.boundaryDescription")}
				</div>
				<div className="flex flex-row items-center gap-2">
					<Button
						size={ButtonSize.ML}
						title={i18n.t("errorPages.boundaryReload")}
						onClick={this.handleReload}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.ML}
						title={i18n.t("errorPages.boundaryRetry")}
						onClick={this.handleRetry}
					/>
				</div>
			</div>
		);
	}
}
